import React, { useEffect, useState } from 'react';

import {
  EnrollmentReceipt as Receipt,
  getEnrollmentReceipt,
  ReceiptRefundReason,
} from '../api/enrollments';
import { formatMoney } from '../../../commodity/format/money';

const REFUND_REASON_LABEL: Record<ReceiptRefundReason, string> = {
  CANCELLATION: 'Cancelación de la inscripción',
  DISPUTE: 'Problema reportado resuelto a tu favor',
  TEACHER_NO_SHOW: 'El profesor no se presentó',
  SUPPORT: 'Reembolso aprobado por soporte',
  OTHER: 'Reembolso',
};

/**
 * D-P07-03 (CTO, 12 Oct 2026): an authorization is not a charge, and a released
 * one is not a refund (D-P07-05). The empty receipt says which case it is.
 */
const noChargeCopy = (receipt: Receipt): string => {
  switch (receipt.state) {
    case 'PendingTeacherAcceptance':
      return 'Tu tarjeta está autorizada, pero todavía no se ha cobrado nada. El cobro se realiza solo si el profesor acepta.';
    case 'TeacherDeclined':
    case 'AcceptanceExpired':
      return 'No se realizó ningún cargo. La autorización de tu tarjeta se liberó.';
    default:
      return 'Esta inscripción aún no tiene cargos.';
  }
};

const formatDate = (iso: string | null): string =>
  iso
    ? new Date(iso).toLocaleString('es-MX', { dateStyle: 'medium', timeStyle: 'short' })
    : 'Fecha no registrada';

/**
 * PRT-07-FR-12 — what the family paid for this enrollment and what came back.
 *
 * Every line is the server's (PRT-BR-P07-17): the charge is the payment
 * reference the provider issued, each refund a recorded refund with its own
 * reference. Nothing here is computed from the price on the page, so the
 * receipt cannot disagree with what actually moved.
 *
 * `refreshKey` lets the screen refetch after something that moves money, such
 * as a cancellation, without this component knowing what happened.
 */
const EnrollmentReceipt: React.FC<{ enrollmentId: string; refreshKey?: string }> = ({
  enrollmentId,
  refreshKey,
}) => {
  const [receipt, setReceipt] = useState<Receipt | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setFailed(false);
    getEnrollmentReceipt(enrollmentId)
      .then((r) => {
        if (!cancelled) setReceipt(r);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [enrollmentId, refreshKey]);

  return (
    <section aria-labelledby="receipt-heading" style={panelStyle}>
      <h2 id="receipt-heading" style={{ marginTop: 0 }}>
        Recibo y movimientos
      </h2>
      {failed ? (
        <p role="alert" style={{ color: '#c92a2a' }}>
          No pudimos cargar tu recibo. Inténtalo de nuevo más tarde.
        </p>
      ) : !receipt ? (
        <p>Cargando recibo…</p>
      ) : !receipt.charge ? (
        <p>{noChargeCopy(receipt)}</p>
      ) : (
        <ReceiptBody receipt={receipt} />
      )}
    </section>
  );
};

const ReceiptBody: React.FC<{ receipt: Receipt }> = ({ receipt }) => {
  const { charge, refunds, totals, currency } = receipt;
  const subject = [receipt.courseTitle, receipt.childName].filter(Boolean).join(' · ');
  return (
    <>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ borderBottom: '2px solid #d1d5da', textAlign: 'left' }}>
            <th style={cell}>Fecha</th>
            <th style={cell}>Concepto</th>
            <th style={{ ...cell, textAlign: 'right' }}>Monto</th>
          </tr>
        </thead>
        <tbody>
          <tr style={rowStyle}>
            <td style={cell}>{formatDate(charge!.chargedAt)}</td>
            <td style={cell}>
              Pago de la inscripción{subject ? ` — ${subject}` : ''}
              <div style={refStyle}>Referencia de pago: {charge!.paymentReference}</div>
            </td>
            <td style={{ ...cell, textAlign: 'right' }}>{formatMoney(charge!.amount, currency)}</td>
          </tr>
          {refunds.map((r, i) => (
            <tr key={`${r.refundedAt}-${i}`} style={rowStyle}>
              <td style={cell}>{formatDate(r.refundedAt)}</td>
              <td style={cell}>
                {REFUND_REASON_LABEL[r.reason] ?? REFUND_REASON_LABEL.OTHER}
                {r.sessionSequence !== undefined &&
                  ` · Sesión ${r.sessionSequence} no impartida, reembolso completado`}
                {r.reference && <div style={refStyle}>Referencia de reembolso: {r.reference}</div>}
              </td>
              <td style={{ ...cell, textAlign: 'right', color: '#2b8a3e' }}>
                −{formatMoney(r.amount, currency)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {receipt.unitemisedRefunded > 0 && (
        <p style={{ fontSize: 13, color: '#666' }}>
          Incluye {formatMoney(receipt.unitemisedRefunded, currency)} reembolsados antes de que
          guardáramos el detalle de cada reembolso.
        </p>
      )}

      <dl aria-label="Totales" style={totalsStyle}>
        <dt>Cobrado</dt>
        <dd>{formatMoney(totals.charged, currency)}</dd>
        <dt>Reembolsado</dt>
        <dd>{formatMoney(totals.refunded, currency)}</dd>
        <dt style={{ fontWeight: 700 }}>Total pagado</dt>
        <dd style={{ fontWeight: 700 }}>{formatMoney(totals.net, currency)}</dd>
      </dl>
    </>
  );
};

const panelStyle: React.CSSProperties = {
  marginTop: 24,
  padding: 16,
  border: '1px solid #e9ecef',
  borderRadius: 8,
};
const cell: React.CSSProperties = { padding: 8, verticalAlign: 'top' };
const rowStyle: React.CSSProperties = { borderBottom: '1px solid #e9ecef' };
const refStyle: React.CSSProperties = { fontSize: 12, color: '#868e96', marginTop: 2 };
const totalsStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '1fr auto',
  gap: '4px 16px',
  marginTop: 12,
};

export default EnrollmentReceipt;
